import { useState } from 'react';

import { FlexAligner, ModalHeader, TabBase, Tabs } from '@scaleaq/scaleui';
import { CameraInfo, InventoryType, PresetInfo } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { useFeedbackAlert } from '@/components';
import { CameraStreamControlButton } from '@/components/cameraStreamWidget/CameraStreamControlButton.tsx';
import { AddOrEditPresetModal } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/AddOrEditPresetModal.tsx';
import { PresetGroupsList } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/PresetGroupsList.tsx';
import { PresetGuide } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/PresetGuide.tsx';
import {
  StyledPresetTabPanel,
  StyledPresetTabs,
} from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/presetModal.styles.ts';
import { presetModalTokens } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/presetModal.tokens.ts';
import { PresetPositionList } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/PresetPositionList.tsx';
import { RemovePresetConfirmModal } from '@/components/cameraStreamWidget/cameraStreamSideControls/presetModal/RemovePresetConfirmModal.tsx';
import { useCameraStreamFrameControlsContext } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraStreamFrameControlsProvider/CameraStreamFrameControlsContext.ts';
import { useOpenModal } from '@/hooks';
import { FixedHeightStyledModal, ModalGridContent } from '@/styles.ts';
import { Icon } from '@/utils';

export const PresetModal = ({ camera }: { camera: CameraInfo }) => {
  const { t } = useTranslation(['cameraControl', 'common']);
  const { frameControlsVisible } = useCameraStreamFrameControlsContext();
  const showAllPresetModalController = useOpenModal();
  const addModalController = useOpenModal();
  const removeModalController = useOpenModal();
  const { setFeedback, clearFeedback, alertProps } = useFeedbackAlert();

  const [selectedTab, setSelectedTab] = useState<'group' | 'guide' | 'single'>('single');
  const [selectedPreset, setSelectedPreset] = useState<PresetInfo>();
  const [presetToDelete, setPresetToDelete] = useState<PresetInfo>();
  const openPresetForm = (preset?: PresetInfo) => {
    setSelectedPreset(preset);
    addModalController.handleOpenModal();
  };
  const openRemovePreset = (preset: PresetInfo) => {
    setPresetToDelete(preset);
    removeModalController.handleOpenModal();
  };

  const closePresetList = () => {
    clearFeedback();
    addModalController.onClose();
    removeModalController.onClose();
    setPresetToDelete(undefined);
    showAllPresetModalController.onClose();
  };

  const showSavedFeedback = ({
    name,
    operation,
  }: {
    name: string;
    operation: keyof typeof savedPresetMessageKey;
  }) => {
    setFeedback({ text: t(savedPresetMessageKey[operation], { name }), variant: 'success' });
  };

  const isCameraCompatibleWithPresets =
    camera.type === InventoryType.OrbitOne ||
    camera.type === InventoryType.OrbitFhdUnderwaterCamera;

  if (!isCameraCompatibleWithPresets) {
    return null;
  }

  return (
    <>
      <CameraStreamControlButton
        hidden={!frameControlsVisible}
        icon={<Icon.MapPinned />}
        label={t('preset.presets')}
        onClick={showAllPresetModalController.handleOpenModal}
      />
      <FixedHeightStyledModal
        closeOnBackdropClick={true}
        closeOnEscapeClick={true}
        open={showAllPresetModalController.open}
        onClose={closePresetList}
        aria-label={t('preset.positionPresets')}
        $width={presetModalTokens.list.width}
        $maxWidth={presetModalTokens.list.maxWidth}
      >
        <ModalHeader title={t('preset.positionPresets')} onClose={closePresetList} />
        <ModalGridContent>
          <StyledPresetTabs>
            <Tabs>
              <TabBase
                key={'CameraPresets'}
                selected={selectedTab === 'single'}
                onClick={() => setSelectedTab('single')}
              >
                {t('preset.singleCameraPresets')}
              </TabBase>
              <TabBase
                key={'GroupPresets'}
                selected={selectedTab === 'group'}
                onClick={() => setSelectedTab('group')}
              >
                {t('preset.groupCameraPresets')}
              </TabBase>
              <TabBase
                key={'PresetGuide'}
                selected={selectedTab === 'guide'}
                onClick={() => setSelectedTab('guide')}
              >
                {t('preset.guide.tab')}
              </TabBase>
            </Tabs>
          </StyledPresetTabs>
          <StyledPresetTabPanel key={selectedTab}>
            {selectedTab === 'single' && (
              <PresetPositionList
                alertProps={alertProps}
                camera={camera}
                onDelete={openRemovePreset}
                onEdit={openPresetForm}
                setFeedback={setFeedback}
              >
                <FlexAligner alignment={'top-right'}>
                  <AddOrEditPresetModal
                    camera={camera}
                    modalController={addModalController}
                    selectedPreset={selectedPreset}
                    onDefineNew={() => openPresetForm()}
                    onSaved={showSavedFeedback}
                  />
                </FlexAligner>
                {presetToDelete && (
                  <RemovePresetConfirmModal
                    modalController={removeModalController}
                    onSaved={showSavedFeedback}
                    preset={presetToDelete}
                  />
                )}
              </PresetPositionList>
            )}
            {selectedTab === 'group' && (
              <PresetGroupsList alertProps={alertProps} setFeedback={setFeedback} />
            )}
            {selectedTab === 'guide' && <PresetGuide />}
          </StyledPresetTabPanel>
        </ModalGridContent>
      </FixedHeightStyledModal>
    </>
  );
};

const savedPresetMessageKey = {
  added: 'common:snackbar.success.added',
  updated: 'common:snackbar.success.updated',
  deleted: 'common:snackbar.success.deleted',
} as const;
